import React from "react";
import styled from "styled-components/macro";
import { IconButton } from "@mui/material";
import RestartIcon from "@mui/icons-material/RestartAlt";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import { TextField } from "styled/TextField";
import BaseURLItem, { ActiveIndicator } from "root/admin/switchAppSource/BaseUrlItem";
import Row from "components/other/Row";
import { HeaderLayout } from "components/header/Header";
import { windowReload } from "tools/windowReload";
import { deleteJsStorage } from "tools/jsUpdater";
import { getStoragedJsVersion } from "tools/storageJsVersion";

const SRow = styled(Row)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  overflow: hidden;
  padding: 12px 20px;

  & > p {
    font-size: 11px;
    color: #999;
    margin: 0;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

type PersonURLs = {
  name: string;
  office?: string;
  home?: string;
};

const peopleURLs: PersonURLs[] = [
  { name: "Production", office: `https://app.duocards.com/static/app/main.chunk.js` },
  { name: "Staging", office: `https://duocards-fe-stg.herokuapp.com/static/app/main.chunk.js` },
  {
    name: "Petr",
    office: `http://192.168.2.216:3000/static/js/bundle.js`,
    home: `http://192.168.0.207:3000/static/js/bundle.js`
  },
  {
    name: "Radim",
    office: `http://192.168.2.136:3000/static/js/bundle.js`,
    home: `http://192.168.1.127:3000/static/js/bundle.js`
  },
  {
    name: "Radek",
    office: `http://192.168.2.140:3000/static/js/bundle.js`,
    home: `http://10.0.0.175:3000/static/js/bundle.js`
  },
  {
    name: "Martin",
    office: `http://192.168.2.179:3000/static/js/bundle.js`,
    home: `http://192.168.1.148:3000/static/js/bundle.js`
  }
];

const runningBaseURL = localStorage.getItem("baseURL");
const allKnownURLs = peopleURLs.flatMap((p) => [p.office, p.home].filter(Boolean));
const isCustomRunningBaseURL: boolean = !allKnownURLs.find((url) => url === runningBaseURL);

const BASE_URL_KEY = "baseURL";

const BaseUrlSettings = () => {
  const [customBaseURL, setCustomBaseURL] = React.useState<string>(runningBaseURL || ""); // last clicked URL or edited URL
  const [prNumber, setPrNumber] = React.useState<string>("");

  const reloadAppFrom = (baseURL: string): void => {
    deleteJsStorage().then(() => {
      localStorage.setItem(BASE_URL_KEY, baseURL);
      windowReload();
    });
  };

  const getPrPreviewUrl = (pr: string): string => {
    return `https://duocards-stg-pr-${pr}.herokuapp.com/static/app/main.chunk.js`;
  };

  const jsVersion = getStoragedJsVersion();

  return (
    <HeaderLayout title="Switch app source">
      <>
        {jsVersion && (
          <Row>
            <p>JS Version: {jsVersion}</p>
          </Row>
        )}
        {!runningBaseURL && <Row>Currently running from default URL</Row>}
        {runningBaseURL && isCustomRunningBaseURL && (
          <SRow onClick={() => reloadAppFrom(runningBaseURL)}>
            <BaseURLItem label="Custom URL" url={runningBaseURL} isActive={true} />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                reloadAppFrom(runningBaseURL);
              }}
            >
              <RestartIcon />
            </IconButton>
          </SRow>
        )}
        {peopleURLs.map((person) => (
          <SRow key={person.name}>
            <BaseURLItem
              label={person.name}
              url={person.office && person.home ? undefined : person.office || person.home}
              isActive={runningBaseURL === person.office || runningBaseURL === person.home}
            />
            <IconsContainer>
              {person.office && (
                <IconButton onClick={() => reloadAppFrom(person.office!)}>
                  <BusinessIcon />
                </IconButton>
              )}
              {person.home && (
                <IconButton onClick={() => reloadAppFrom(person.home!)}>
                  <HomeIcon />
                </IconButton>
              )}
            </IconsContainer>
          </SRow>
        ))}
        <SRow>
          <TextField
            label="PR Preview (enter PR number)"
            value={prNumber}
            onChange={(e) => setPrNumber(e.target.value)}
            placeholder="e.g., 1673"
            style={{ flex: 1 }}
          />
          <IconButton
            disabled={!prNumber}
            onClick={(e) => {
              e.stopPropagation();
              reloadAppFrom(getPrPreviewUrl(prNumber));
            }}
          >
            <RestartIcon />
          </IconButton>
        </SRow>
        <SRow>
          {customBaseURL === runningBaseURL && <ActiveIndicator />}
          <TextField
            label="Set custom app URL"
            value={customBaseURL}
            onChange={(e) => setCustomBaseURL(e.target.value)}
            style={{ flex: 1 }}
          />
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              reloadAppFrom(customBaseURL);
            }}
          >
            <RestartIcon />
          </IconButton>
        </SRow>
      </>
    </HeaderLayout>
  );
};

export default BaseUrlSettings;
